import {
  Form,
  Checkbox,
  Space,
  Row,
  Col,
  Button,
  Pagination,
  Modal,
  Input,
  List,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { getserialesByserial } from "../../../services/seriales";


const Seriales = ({
  productoselect,
  seriales,
  agregarserial,
  arrayproductosfacturar,
  onFetchSeriales,
  current,
  totalpagina,
  setPagination,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [filteredSeriales, setFilteredSeriales] = useState(seriales);
  const [selectAll, setSelectAll] = useState(false);
  const [allSeriales, setAllSeriales] = useState([]);
  const [totalSerialesCount, setTotalSerialesCount] = useState(0);

  const showModal = async () => {
    setLoading(true);
    try {
      setAllSeriales(seriales);
      setTotalSerialesCount(totalpagina);
      
      // Usar la función original para cargar la primera página
      await onFetchSeriales(productoselect?.id);
      setIsModalVisible(true);
      setFilteredSeriales(seriales);
      setSearchText("");
      setSelectAll(false);
    } catch (error) {
      console.error("Error fetching seriales:", error);
      // Fallback a la función original si hay error
      await onFetchSeriales(productoselect?.id);
      setIsModalVisible(true);
      setFilteredSeriales(seriales);
      setSearchText("");
      setSelectAll(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSearchText("");
    setFilteredSeriales(seriales);
    setSelectAll(false);
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (value.trim() === "") {
      // Volver a la paginación normal
      setFilteredSeriales(seriales);
      return;
    }

    setLoading(true);
    try {
      const result = await getserialesByserial(value.trim());

      const filtered = productoselect?.id
        ? result.filter((s) => s.producto?.id === productoselect.id)
        : result;
      setFilteredSeriales(filtered);
    } catch (error) {
      setFilteredSeriales([]);
      console.error("Error buscando serial:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilteredSeriales(seriales);
    setTotalSerialesCount(totalpagina);
  }, [seriales, totalpagina]);

  const isSerialSelected = (serialId) => {
    return (
      Array.isArray(arrayproductosfacturar) &&
      arrayproductosfacturar.some(
        (item) =>
          Array.isArray(item.seriales) &&
          item.seriales.some((s) => s.id === serialId)
      )
    );
  };

  const handleSelectAll = async (checked) => {
    setSelectAll(checked);
    if (checked) {
      if (searchText.trim() === "") {
        // Obtener todos los seriales disponibles (no solo la página actual)
        setLoading(true);
        try {
          // Usar la misma función de consulta pero con limit=-1 para obtener todos
          const allSerialesData = await onFetchSeriales(productoselect?.id, -1);
          
          // Seleccionar todos los seriales obtenidos
          for (const serial of allSerialesData) {
            if (!isSerialSelected(serial.id)) {
              agregarserial(serial, true, false);
            }
          }
        } catch (error) {
          console.error("Error fetching all seriales:", error);
          message.error("Error al obtener todos los seriales");
        } finally {
          setLoading(false);
        }
      } else {
        // Seleccionar todos los seriales filtrados por búsqueda
        for (const serial of filteredSeriales) {
          if (!isSerialSelected(serial.id)) {
            agregarserial(serial, true, false);
          }
        }
      }
      // Forzar actualización del estado después de un breve delay
      setTimeout(() => {
        setSelectAll(true);
      }, 100);
    } else {
      // Deseleccionar todos los seriales
      const serialesToDeselect = searchText.trim() === "" 
        ? await onFetchSeriales(productoselect?.id, -1) // Obtener todos para deseleccionar
        : filteredSeriales;
      
      // Deseleccionar todos los seriales disponibles
      for (const serial of serialesToDeselect) {
        if (isSerialSelected(serial.id)) {
          agregarserial(serial, false, false);
        }
      }
      // Forzar actualización del estado después de un breve delay
      setTimeout(() => {
        setSelectAll(false);
      }, 100);
    }
  };
  const areAllSelected = () => {
    const serialesToCheck = searchText.trim() === "" ? seriales : filteredSeriales;
    if (serialesToCheck.length === 0) return false;
    
    // Verificar que todos los seriales estén seleccionados
    return serialesToCheck.every((serial) => isSerialSelected(serial.id));
  };

  const areSomeSelected = () => {
    const serialesToCheck = searchText.trim() === "" ? seriales : filteredSeriales;
    if (serialesToCheck.length === 0) return false;
    
    // Verificar que al menos algunos seriales estén seleccionados
    return serialesToCheck.some((serial) => isSerialSelected(serial.id));
  };

  useEffect(() => {
    // Solo actualizar el estado del checkbox si hay seriales para mostrar
    if (filteredSeriales.length > 0) {
      const allSelected = areAllSelected();
      const someSelected = areSomeSelected();
      
      if (allSelected) {
        setSelectAll(true);
      } else if (someSelected) {
        setSelectAll(false);
      } else {
        setSelectAll(false);
      }
    } else {
      setSelectAll(false);
    }
  }, [filteredSeriales, arrayproductosfacturar, seriales, searchText]);

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Button type="primary" onClick={showModal} loading={loading}>
        Ver Seriales
      </Button>

      <Modal
        title="Seriales Disponibles"
        open={isModalVisible}
        onCancel={handleCancel}
        width={800}
        footer={null}
      >
        <Input
          placeholder="Buscar serial"
          prefix={<SearchOutlined />}
          onChange={handleSearch}
          value={searchText}
          style={{ marginBottom: 16 }}
          allowClear
        />

        {filteredSeriales.length > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: "8px 0",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Checkbox
              checked={selectAll}
              indeterminate={areSomeSelected() && !areAllSelected()}
              onChange={(e) => handleSelectAll(e.target.checked)}
              loading={loading}
            >
              <strong>
                Seleccionar todos los seriales
                {searchText.trim() === "" && ` (${seriales.length} de ${totalSerialesCount} total disponibles)`}
                {searchText.trim() !== "" && ` (${filteredSeriales.length} encontrados)`}
              </strong>
            </Checkbox>
           
          </div>
        )}

        <List
          loading={loading}
          dataSource={filteredSeriales}
          renderItem={(item) => (
            <List.Item>
              <Checkbox
                onChange={(e) => agregarserial(item, e.target.checked)}
                checked={isSerialSelected(item.id)}
              >
                {item.serial}
              </Checkbox>
            </List.Item>
          )}
        />

        {searchText.trim() === "" && (
          <Pagination
            onChange={(page) => {
              setPagination((prev) => ({ ...prev, current: page }));
            }}
            current={current}
            total={totalpagina}
            pageSize={20}
            style={{ marginTop: 16, textAlign: "right" }}
          />
        )}
      </Modal>
    </Space>
  );
};

export default Seriales;
